/**
 * Deposit reconciliation query + refund mutation layer. Reads key under `["venue", venueId, "deposits", …]`
 * so the shell's `eventQueryKeyMap` (`billing.guest_payment.*` → `["venue", venueId, "deposits"]`)
 * invalidates the report when a charge/refund/forfeit lands. A manager refund refetches the report.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { depositsApi, type Reconciliation } from "./depositsApi";

export const depositsKeys = {
  reconciliation: (venueId: string, from: string, to: string) =>
    ["venue", venueId, "deposits", "reconciliation", from, to] as const,
};

export function useReconciliation(venueId: string, from: string, to: string, enabled = true) {
  return useQuery<Reconciliation>({
    queryKey: depositsKeys.reconciliation(venueId, from, to),
    queryFn: ({ signal }) => depositsApi.reconcile(venueId, from, to, signal),
    enabled: enabled && from !== "" && to !== "",
    staleTime: 30_000,
  });
}

export function useManagerRefund(venueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      bookingRef: string;
      amountMinor: number;
      currency: string;
      reason: string;
    }) =>
      depositsApi.refund(venueId, vars.bookingRef, vars.amountMinor, vars.currency, vars.reason),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["venue", venueId, "deposits"] });
    },
  });
}
